import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, Select, Spin } from "antd";
import {
  createHealthClientDiagnosis,
  fetchHealthDiagnosisCatalog,
  fetchHealthDiagnosisTypes,
  updateHealthClientDiagnosis
} from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const normalizeType = (row) => ({
  id: toNumber(pick(row, "id", "Id")) ?? 0,
  name: pick(row, "name", "Name") ?? ""
});

const normalizeCatalog = (row) => {
  const idText = toNumber(pick(row, "idText", "IdText"));
  const kod = pick(row, "kod", "Kod") ?? "";
  const phrase = pick(row, "phrase", "Phrase") || pick(row, "name", "Name") || "";
  return {
    idText,
    kod,
    phrase,
    label: kod ? `${kod} — ${phrase}` : phrase
  };
};

/**
 * Форма добавления/редактирования диагноза.
 * embedded — без кнопок (кнопки в drawer).
 */
const ClientDiagnosisForm = ({
  idClient,
  item = null,
  form: formProp,
  embedded = false,
  onCancel,
  onSaved,
  onSavingChange
}) => {
  const [innerForm] = Form.useForm();
  const form = formProp || innerForm;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [types, setTypes] = useState([]);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const isEdit = item?.idStr != null;

  const setSavingState = (value) => {
    setSaving(value);
    onSavingChange?.(value);
  };

  useEffect(() => {
    let cancelled = false;
    fetchHealthDiagnosisTypes()
      .then((rows) => {
        if (!cancelled) setTypes((Array.isArray(rows) ? rows : []).map(normalizeType));
      })
      .catch(() => {
        if (!cancelled) setTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      kod: item?.kod ?? "",
      name: item?.name ?? "",
      name2: item?.name2 ?? "",
      vid: item?.vid ?? 0
    });
    setCatalogOptions([]);
    setError("");
  }, [form, item]);

  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    []
  );

  const typeOptions = types.map((t) => ({ value: t.id, label: t.name }));

  const searchCatalog = (q) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCatalogLoading(true);
      fetchHealthDiagnosisCatalog(q)
        .then((data) => {
          setCatalogOptions((Array.isArray(data) ? data : []).map(normalizeCatalog));
        })
        .catch(() => setCatalogOptions([]))
        .finally(() => setCatalogLoading(false));
    }, 250);
  };

  const onOpenCatalog = (visible) => {
    if (visible && !catalogOptions.length) searchCatalog("");
  };

  const onPickCatalog = (idText) => {
    const found = catalogOptions.find((x) => x.idText === idText);
    if (!found) return;
    form.setFieldsValue({
      kod: found.kod,
      name: found.phrase,
      name2: ""
    });
  };

  const handleFinish = async (values) => {
    if (saving) return;
    setError("");
    setSavingState(true);
    try {
      const body = {
        kod: values.kod?.trim() || "",
        name: values.name?.trim() || "",
        name2: values.name2?.trim() || "",
        vid: values.vid ?? 1
      };
      if (isEdit) {
        await updateHealthClientDiagnosis(idClient, item.idStr, body);
      } else {
        await createHealthClientDiagnosis(idClient, body);
      }
      onSaved?.(isEdit ? "updated" : "created");
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSavingState(false);
    }
  };

  return (
    <div className={`client-diagnosis-form${embedded ? " client-diagnosis-form--embedded" : ""}`}>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      <Form form={form} layout="vertical" className="client-diagnosis-form__fields" onFinish={handleFinish}>
        <Form.Item label="Из справочника МКБ">
          <Select
            showSearch
            allowClear
            filterOption={false}
            placeholder="Код или название…"
            loading={catalogLoading}
            options={catalogOptions.map((o) => ({ value: o.idText, label: o.label }))}
            onSearch={searchCatalog}
            onOpenChange={onOpenCatalog}
            onChange={(value) => {
              if (value != null) onPickCatalog(value);
            }}
            notFoundContent={catalogLoading ? <Spin size="small" /> : "Нет совпадений"}
          />
        </Form.Item>

        <div className="client-diagnosis-form__row">
          <Form.Item name="kod" label="Код" className="client-diagnosis-form__kod">
            <Input maxLength={20} />
          </Form.Item>
          <Form.Item name="vid" label="Вид" rules={[{ required: true, message: "Укажите вид" }]}>
            <Select options={typeOptions} />
          </Form.Item>
        </div>

        <Form.Item
          name="name"
          label="Наименование"
          rules={[{ required: true, message: "Укажите наименование" }]}
        >
          <Input.TextArea rows={2} maxLength={255} />
        </Form.Item>

        <Form.Item name="name2" label="Уточнение">
          <Input.TextArea rows={2} maxLength={255} />
        </Form.Item>
      </Form>

      {!embedded ? (
        <div className="client-diagnosis-form__actions">
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            ОК
          </Button>
          <Button onClick={onCancel} disabled={saving}>
            Отмена
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ClientDiagnosisForm;
